import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { ElectionService } from 'src/app/services/election.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { RDColumn } from 'src/app/models/RDTable';
import { MatSort } from '@angular/material/sort';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { IReportFirstPreference } from 'src/app/interfaces/IReportFirstPreference';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';

@Component({
  selector: 'lc-votes-won-table',
  templateUrl: './lc-votes-won-table.component.html',
  styleUrls: ['./lc-votes-won-table.component.scss']
})
export class LcVotesWonTableComponent implements AfterViewInit, OnDestroy {

  @Input() isSmallTable = true;
  @Input() title = 'First Preference Votes';


  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public firstPreferences: IReportFirstPreference[] = [];
  public independentDetails: IReportFirstPreference[] = [];
  public isIndependentsExpanded = false;

  public dataTable: IReportFirstPreference[] = [];
  public dataSource = new MatTableDataSource<IReportFirstPreference>();
  public columns: ITableHeader[] = [];
  public displayedColumns: string[] = [];
  public dataIsLoaded = true;

  constructor(private electionService: ElectionService) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }


  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }


  private loadChart(page: ISelectPage): void {
    this.columns = [];
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    if (page.pageType === PageTypeEnum.CountLCStateWide) {
      const allParties = election?.lcElectionCount?.parties ?? [];
      const { independents, others } = allParties.reduce(
        (acc, party) => {
          if (party.partyId === 'IND') {
            acc.independents.push(party);
          } else {
            acc.others.push(party);
          }
          return acc;
        },
        { independents: [] as typeof allParties, others: [] as typeof allParties }
      );

      const nonIndependentEntries = others.map(party => ({
        district: null,
        partyId: party.partyId,
        party: party.partyName,
        colourCode: party.colourCode,
        candidateName: null,
        votes: party.votes ?? 0,
        share: party.partyShare ?? 0,
        change: 0
      }));

      const totalVotes = allParties.reduce((sum, party) => sum + (party.votes ?? 0), 0);
      this.independentDetails = independents.map(party => ({
        district: null,
        partyId: party.partyId,
        party: party.partyName,
        colourCode: party.colourCode,
        candidateName: null,
        votes: party.votes ?? 0,
        share: totalVotes > 0 ? ((party.votes ?? 0) / totalVotes) * 100 : 0,
        change: 0
      }));
      const independentEntry =
        independents.length > 0
          ? [{
              district: null,
              partyId: 'IND',
              party: 'Independents',
              colourCode: independents[0].colourCode,
              candidateName: null,
              votes: independents.reduce((sum, party) => sum + (party.votes ?? 0), 0),
              share: totalVotes > 0
                ? (independents.reduce((sum, party) => sum + (party.votes ?? 0), 0) / totalVotes) * 100
                : 0,
              change: 0
            }]
          : [];

      this.firstPreferences = [...nonIndependentEntries, ...independentEntry];
      this.firstPreferences.sort((a, b) => b.votes - a.votes);
      this.isIndependentsExpanded = false;
      this.columns.push({
        column: 'colourCode',
        label: '',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 20,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: true,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: 'party',
        label: 'Affiliation',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 60,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: 'votes',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 60,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: 'share',
        label: 'Share',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 60,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: true,
        colourCode: null
      });
    }


    this.displayedColumns =
      this.independentDetails.length > 0
        ? ['expand', ...this.columns.map(c => c.column)]
        : this.columns.map(c => c.column);
    this.updateDataSource();
  }

  toggleIndependents(): void {
    this.isIndependentsExpanded = !this.isIndependentsExpanded;
    this.updateDataSource();
  }

  private updateDataSource(): void {
    if (this.isIndependentsExpanded && this.independentDetails.length > 0) {
      const independentsIndex = this.firstPreferences.findIndex(p => p.partyId === 'IND' && p.party === 'Independents');
      if (independentsIndex !== -1) {
        const updatedData = [...this.firstPreferences];
        updatedData.splice(independentsIndex + 1, 0, ...this.independentDetails);
        this.dataTable = updatedData;
      } else {
        this.dataTable = this.firstPreferences;
      }
    } else {
      this.dataTable = this.firstPreferences;
    }
    if (this.dataTable?.length > 0) {
      this.dataSource = new MatTableDataSource<IReportFirstPreference>(this.dataTable);
    }
  }

  isIndependentsRow(element: IReportFirstPreference): boolean {
    return element.partyId === 'IND' && element.party === 'Independents';
  }

  isIndividualIndependent(element: IReportFirstPreference): boolean {
    return (
      element.partyId === 'IND' &&
      element.party !== 'Independents' &&
      this.independentDetails.some(ind => ind.party === element.party)
    );
  }

  isNumberType = (val: number | string): boolean => typeof val === 'number';

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
